import { createFileRoute } from '@tanstack/react-router'
import { useEffect, useMemo } from 'react'

import { useUpdatePointOfSale } from '@/api/api-mutations'
import { useGetPointOfSale, useListSalesChannels } from '@/api/react-query'
import { Button } from '@/components/base/button'
import { Drawer } from '@/components/base/drawer'
import { Heading } from '@/components/base/heading'
import { toast } from '@/components/base/toast'
import { FormContainer, FormFieldGroup, useAppForm } from '@/components/form/form'
import { RouteModal, RouteModalFormBlocker, useRouteModal } from '@/components/route-modal'
import { useI18n } from '@/i18n/i18n-react'

export const Route = createFileRoute('/_authenticated/settings/point-of-sale/$pointOfSaleUuid/edit')({
	component: RouteComponent,
	staticData: {
		crumb: ({ context: { L } }) => L.Settings.PointOfSale.EditTitle()
	}
})

function RouteComponent() {
	return (
		<RouteModal asDrawer>
			<EditPointOfSaleForm />
		</RouteModal>
	)
}

function EditPointOfSaleForm() {
	const { L } = useI18n()
	const { handleSuccess } = useRouteModal()
	const { pointOfSaleUuid } = Route.useParams()

	const pointOfSale = useGetPointOfSale({ pointOfSaleUuid })
	const salesChannels = useListSalesChannels()
	const updatePointOfSale = useUpdatePointOfSale()

	const salesChannelOptions = useMemo(() => {
		return (
			salesChannels.data?.map(channel => ({
				value: channel.uuid,
				label: channel.name
			})) || []
		)
	}, [salesChannels.data])

	const form = useAppForm({
		defaultValues: {
			name: '',
			identifier: '',
			salesChannelUuids: [] as string[]
		},
		onSubmit: async ({ value }) => {
			const result = await updatePointOfSale({
				path: { pointOfSaleUuid },
				body: {
					name: value.name,
					identifier: value.identifier || undefined,
					salesChannelUuids: value.salesChannelUuids
				}
			})
			if (result.isSuccess) {
				toast.success(L.Settings.PointOfSale.EditSuccess())
				handleSuccess()
			} else {
				toast.error((result.response as any)?.message ?? L.Error.SomethingWentWrong())
			}
		}
	})

	// Hydrate via `reset` (not `setFieldValue`) so the loaded values become the
	// form's baseline defaults. Otherwise the form is considered changed on open
	// and cancelling shows a false "Unsaved changes" prompt. Guard on
	// `isDefaultValue` so a background refetch (which produces a new data
	// reference) can't overwrite in-progress edits: once the user has diverged
	// from defaults we stop re-hydrating.
	useEffect(() => {
		if (!pointOfSale.data) return
		if (!form.state.isDefaultValue) return
		form.reset({
			name: pointOfSale.data.name,
			identifier: pointOfSale.data.identifier ?? '',
			salesChannelUuids: pointOfSale.data.salesChannels?.map(sc => sc.uuid) ?? []
		})
	}, [pointOfSale.data, form])

	return (
		<form.AppForm>
			<RouteModalFormBlocker form={form as any} />
			<Drawer.Header>
				<Drawer.Title asChild>
					<Heading>{L.Settings.PointOfSale.EditTitle()}</Heading>
				</Drawer.Title>
			</Drawer.Header>
			<Drawer.Body className='flex-1 overflow-auto'>
				<FormContainer>
					<FormFieldGroup>
						<form.AppField
							name='name'
							validators={{ onChange: ({ value }) => (!value ? L.General.Required() : undefined) }}
							children={field => <field.TextField autoFocus label={L.Fields.Name()} />}
						/>
					</FormFieldGroup>
					<FormFieldGroup>
						<form.AppField
							name='identifier'
							children={field => (
								<field.TextField label={L.Settings.PointOfSale.Column.Identifier()} optional />
							)}
						/>
					</FormFieldGroup>
					<FormFieldGroup>
						<form.AppField
							name='salesChannelUuids'
							children={field => (
								<field.ComboboxField
									label={L.Fields.SalesChannel()}
									placeholder={L.Settings.PointOfSale.Create.SelectSalesChannels()}
									multiple
									optional
									options={salesChannelOptions}
								/>
							)}
						/>
					</FormFieldGroup>
				</FormContainer>
			</Drawer.Body>
			<Drawer.Footer>
				<div className='flex items-center justify-end gap-x-2'>
					<Drawer.Close asChild>
						<Button variant='secondary' size='small'>
							{L.General.Cancel()}
						</Button>
					</Drawer.Close>
					<form.SaveButton />
				</div>
			</Drawer.Footer>
		</form.AppForm>
	)
}
